import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import clsx from 'clsx';
import { matchPath } from 'react-router-dom';
import { Drawer, Collapse, Box, IconButton, List, Grid } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountCircle from '@mui/icons-material/AccountCircle';

import { getQuickbooksAccountIsConnected } from 'services/integrations/quickbooks';
import { isTestEnv } from 'services/api/testEnv';
import { getActiveUser } from 'services/user/redux';
import { hasPermission } from 'services/permissions';
import { getShippingIntegration } from 'services/integrations/shipping/redux';
import { getXeroAccountIsConnected } from 'services/integrations/xero';
import { getSettingsCompany } from 'services/settings/company/redux';
import { getCommerceAccountIsOrWasConnected } from 'services/commerce';
import { useFeatureAccess } from 'helpers/serviceTierHooks';

import { DRAWER_ITEMS } from './consts';
import { useAddDrawerFooterStyle, useAppDrawerStyle } from './styled';

export const sideDrawerStatus = React.createContext({ drawerExpanded: false });

import DrawerSubItem, { DrawerSubItemType } from './DrawerSubItem';
import DrawerItem, { DrawerItemType } from './DrawerItem';
import DrawerNav from './DrawerNav';
import ProfileMenu from '../FBOProfileMenu/FBOProfileMenu';

import { ActiveUser } from 'services/user';
import { FBOLogoHorizontal, FBOLogoNoText } from 'ui/theme/images';
import FBOButton from '../FBOButton/FBOButton';
import { useFlags } from 'helpers/useFlags';
import { getInsightsEntitlement } from 'services/insights';

interface AppDrawerProps {
  drawerExpanded: boolean;
  drawerPersistent: boolean;
  onDrawerExpand(expanded: boolean, persistent: boolean): void;
  user?: ActiveUser;
  activePathName: string;
}

const FBODrawer: React.FunctionComponent<AppDrawerProps> = ({
  drawerExpanded,
  onDrawerExpand,
  drawerPersistent,
  activePathName,
}) => {
  const classes = useAppDrawerStyle();

  const quickbooksConnected = useSelector(getQuickbooksAccountIsConnected);
  const xeroConnected = useSelector(getXeroAccountIsConnected);
  const commerceConnected = useSelector(getCommerceAccountIsOrWasConnected);
  const connectedShippingChannels = useSelector(getShippingIntegration);
  const insightsEntitlement = useSelector(getInsightsEntitlement);

  const {
    permissions: userPermissions,
    isAdmin,
    isExecutor,
    user: activeUser,
  } = useSelector(getActiveUser);

  const hasConnectedShippingChannels = useMemo(
    () => connectedShippingChannels.connections.length > 0,
    [connectedShippingChannels]
  );

  const [expandedItem, setExpandedItem] = React.useState<number | null>(null);
  const [hoverExpanded, setHoverExpanded] = React.useState<boolean>(false);

  const { name: companyName, useMultiCurrency } =
    useSelector(getSettingsCompany);

  const [isProfileMenuOpen, setIsProfileMenuOpen] =
    React.useState<boolean>(false);

  const anchorEl = useRef<null | HTMLDivElement>(null);

  const flags = useFlags();
  const featureAccess = useFeatureAccess();
  const footerClasses = useAddDrawerFooterStyle({
    drawerExpanded,
    isProfileMenuOpen,
  });

  const filterNavigationItems = useCallback(
    (items: DrawerItemType[]) => {
      return items
        .filter((item) => {
          if (!item || typeof item !== 'object') return false;

          const { permissions = [], subitems = [], visible = true } = item;

          // Skip if item is explicitly hidden by feature access
          if (visible === false) return false;

          // Skip if user doesn't have required permissions
          if (!hasPermission(permissions, userPermissions, isAdmin)) {
            return false;
          }

          // Filter subitems
          const validSubitems = subitems.filter((subitem) =>
            hasPermission(subitem.permissions || [], userPermissions, isAdmin)
          );

          // Keep the item if it has no subitems or has valid subitems
          return subitems.length === 0 || validSubitems.length > 0;
        })
        .map((item) => {
          // Return items with filtered subitems
          const validSubitems = (item.subitems || []).filter((subitem) =>
            hasPermission(subitem.permissions || [], userPermissions, isAdmin)
          );
          return { ...item, subitems: validSubitems };
        });
    },
    [userPermissions, isAdmin]
  );

  const { topItems, bottomItems } = useMemo(() => {
    const items = DRAWER_ITEMS({
      quickbooksConnected,
      isTestEnv: isTestEnv(),
      hasConnectedShippingChannels,
      xeroConnected,
      isMultiCurrencyVisible: useMultiCurrency,
      showAiInsights: flags.aiInsights && !!insightsEntitlement,
      showTemplates: flags.templateDesigner,
      showTransferOrders:
        flags.transferOrders && featureAccess.canAccessTransferOrders,
      featureAccess,
    });

    return {
      topItems: items?.filter((i) => !i?.placement || i?.placement === 'top'),
      bottomItems: items?.filter((i) => i?.placement === 'bottom'),
    };
  }, [
    quickbooksConnected,
    hasConnectedShippingChannels,
    xeroConnected,
    commerceConnected,
    useMultiCurrency,
    flags.aiInsights,
    insightsEntitlement,
    flags.templateDesigner,
    flags.transferOrders,
    featureAccess,
  ]);

  useEffect(() => {
    const allItems = topItems.concat(bottomItems);
    // Filter items the same way as in rendering to maintain consistent indices
    const filteredItems = filterNavigationItems(allItems);

    // find index of active item in the filtered array
    filteredItems.forEach((value, index) => {
      value?.subitems?.forEach((subitem: DrawerSubItemType) => {
        if (subitem.route === activePathName) {
          setExpandedItem(index);
        }
      });
    });
  }, [activePathName, topItems, bottomItems, filterNavigationItems]);

  const checkIfRouteIsActive = useCallback(
    (path: string | null) => {
      if (!path) {
        return false;
      }
      return matchPath(activePathName, path);
    },
    [activePathName]
  );

  const handleItemClicked = useCallback(
    (id: number) => () => {
      const newActiveItem: number | null = id === expandedItem ? null : id;
      setExpandedItem(newActiveItem);
    },
    [expandedItem]
  );

  const handleDrawerExpand = useCallback(
    (expanded: boolean, persistent: boolean) => {
      onDrawerExpand(expanded, persistent);
    },
    [onDrawerExpand]
  );

  let timeout: any = null;

  const handleMouseEnter = useCallback(
    (e: any) => {
      const el = document.getElementById('expand-toggle-button');
      const topOfExpandButton = el?.getBoundingClientRect()?.y;

      if (el && topOfExpandButton && e.clientY < topOfExpandButton) {
        timeout = setTimeout(() => {
          handleDrawerExpand(true, false);
          setHoverExpanded(true);
        }, 500);
      }
    },
    [handleDrawerExpand]
  );

  const handleMouseLeave = useCallback(() => {
    clearTimeout(timeout);
    handleDrawerExpand(false, false);
    setHoverExpanded(false);
  }, [handleDrawerExpand]);

  const drawerExpandedValue = useMemo(
    () => ({ drawerExpanded: drawerExpanded }),
    [drawerExpanded]
  );

  const renderDrawerItems = useCallback(
    (items: DrawerItemType[], offset?: number) => {
      const renderItems = () => {
        // First filter the navigation items
        const filteredItems = filterNavigationItems(items);

        // Then map them to components
        return filteredItems.map((item, index) => {
          // Type guard to ensure we have a valid DrawerItemType
          if (!item || typeof item !== 'object') return null;

          const {
            text = '',
            subitems = [],
            onlyExecutor,
            route,
            icon,
            link,
            visible = true,
          } = item;

          // subitems are already filtered in filterNavigationItems
          const filteredSubitems = subitems.filter((subitem) => {
            // Allow sandbox components even with empty text
            if (subitem.component) return subitem.visible !== false;

            // Skip subitems with empty text (non-component items)
            if (!subitem.text || subitem.text.trim() === '') return false;

            // Skip if explicitly not visible
            if (subitem.visible === false) return false;

            return true;
          });

          // Only accessible to user with executor property
          if (onlyExecutor && !isExecutor) {
            return null;
          }

          const realIndex = _.isNil(offset) ? index : index + offset;
          const isActive = route ? !!checkIfRouteIsActive(route) : false;

          return (
            <sideDrawerStatus.Provider
              value={drawerExpandedValue}
              key={`${text}-${index}`}
            >
              <DrawerItem
                text={text}
                icon={icon}
                to={route}
                visible={visible}
                itemExpanded={expandedItem === realIndex}
                active={isActive}
                handleClick={() => {
                  return handleItemClicked(realIndex)();
                }}
                dataQa={text}
                link={link}
              />
              {filteredSubitems.length > 0 && (
                <List
                  component="div"
                  className="sandbox-container"
                  disablePadding
                >
                  <Collapse
                    in={
                      (expandedItem === realIndex && drawerExpanded) ||
                      text === 'Sample Database'
                    }
                    timeout="auto"
                    className={classes.expander}
                  >
                    {filteredSubitems.map((subItem, subIndex) => (
                      <DrawerSubItem
                        key={`${subItem.text}-${subIndex}`}
                        text={subItem.text}
                        to={subItem.route}
                        visible={subItem.visible}
                        active={!!checkIfRouteIsActive(subItem.route || '')}
                        secondary
                        permissions={subItem.permissions}
                        dataQa={`${text}-${subItem.text}`}
                        component={subItem.component}
                      />
                    ))}
                  </Collapse>
                </List>
              )}
            </sideDrawerStatus.Provider>
          );
        });
      };

      return <List disablePadding>{renderItems()}</List>;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      userPermissions,
      isAdmin,
      isExecutor,
      checkIfRouteIsActive,
      expandedItem,
      handleItemClicked,
      drawerExpanded,
    ]
  );

  const handleIconButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      return hoverExpanded && drawerPersistent
        ? handleDrawerExpand(!drawerExpanded, true)
        : hoverExpanded && !drawerPersistent
        ? handleDrawerExpand(drawerExpanded, true)
        : handleDrawerExpand(!drawerExpanded, true);
    },
    [drawerExpanded, drawerPersistent, handleDrawerExpand, hoverExpanded]
  );

  const closeProfileMenu = (event: { currentTarget: any }, reason: string) => {
    if (reason === 'backdropClick' || event.currentTarget !== anchorEl) {
      setIsProfileMenuOpen(false);
    }
  };

  const openProfileMenu = (e: React.MouseEvent<HTMLElement | MouseEvent>) => {
    if (!isProfileMenuOpen && e.currentTarget === anchorEl.current) {
      setIsProfileMenuOpen(!isProfileMenuOpen);
    }
  };

  return (
    <DrawerNav persisted={drawerPersistent}>
      <Drawer
        variant="permanent"
        anchor="left"
        className={clsx('redesign', {
          drawerOpen: drawerExpanded,
          drawerClose: !drawerExpanded,
        })}
        classes={{
          paper: clsx(classes.drawer, {
            [classes.drawerOpen]: drawerExpanded,
            [classes.drawerClose]: !drawerExpanded,
          }),
        }}
        open={drawerExpanded}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Box
          sx={{
            scrollbarWidth: 'thin',
            marginRight: '-2px',
            padding: '8px 10px 0 8px',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: '1',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {drawerExpanded ? (
            <img
              src={FBOLogoHorizontal}
              alt="FishBowl"
              width="175px"
              style={{
                maxWidth: '163px',
                margin: '16px 4px 24px 0px',
                alignSelf: 'center',
              }}
              data-qa={'fbo-logo-horizontal'}
            />
          ) : (
            <img
              src={FBOLogoNoText}
              alt="FishBowl"
              style={{
                maxWidth: '56px',
                margin: '16px 0px 24px 0px',
                display: 'flex',
                alignSelf: 'center',
              }}
              data-qa={'fbo-logo-no-text'}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%',
            }}
          >
            {renderDrawerItems(topItems)}
            {renderDrawerItems(bottomItems, topItems.length)}
          </Box>
        </Box>

        <Box className={footerClasses.drawerFooter}>
          {commerceConnected && (
            <FBOButton
              variant="primary"
              color="neutral"
              href={process.env.REACT_APP_SELLWARE_URL}
              className={footerClasses.goToCommerceButton}
              data-qa="go-to-commerce"
            >
              Manage Channels
            </FBOButton>
          )}
          <Box className={footerClasses.profileAndExpandButtonContainer}>
            <Box
              ref={anchorEl}
              className={clsx(footerClasses.profile, {
                active: isProfileMenuOpen,
              })}
              onClick={openProfileMenu}
            >
              <IconButton
                edge="end"
                className={footerClasses.avatar}
                aria-label="open profile"
                aria-haspopup="true"
                color="inherit"
                data-qa="open-profile-icon"
                size="large"
                disableRipple
              >
                <AccountCircle />
              </IconButton>
              <Grid container xs={12} className={footerClasses.names}>
                <Grid item xs={12} className="name user-name">
                  {activeUser?.firstName} {activeUser?.lastName}
                </Grid>
                <Grid item xs={12} className="name company-name">
                  {companyName}
                </Grid>
              </Grid>
              <ProfileMenu
                anchorEl={anchorEl.current}
                isMenuOpen={isProfileMenuOpen}
                onClose={closeProfileMenu}
              />
            </Box>
            <IconButton
              id="expand-toggle-button"
              aria-label="open drawer"
              onClick={handleIconButtonClick}
              className={footerClasses.expandIcon}
              size="large"
              disableRipple
            >
              {drawerPersistent ? (
                <ChevronLeftIcon />
              ) : drawerExpanded && !hoverExpanded ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </Box>
        </Box>
      </Drawer>
    </DrawerNav>
  );
};

export default React.memo(FBODrawer);
